import get from 'lodash/get';

import {
  ajaxFetch,
  buildUrlParamsString,
  fetchDaffy,
  fetchTyphon,
  typhonAjaxFetch,
} from '~/helpers/api';

import { routes, typhonRoutes } from '~/helpers/routes';
import { workflowRoutes } from '~/helpers/api-new/modules/workflows/workflowRoutes';

const apiCalls = {
  ai: {
    promptTemplates: {
      get: ({ typhonDomain }) => fetchTyphon(`${typhonDomain}${typhonRoutes.ai.promptTemplates.get}`),
      archive: ({ typhonDomain, featureCategory }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.ai.promptTemplates.archive(featureCategory)}`, {
        method: 'DELETE',
        json: false,
      }),
    },
    promptTemplateVersions: {
      get: ({ typhonDomain, isSystemPrompt, featureCategory }) => fetchTyphon(`${typhonDomain}${typhonRoutes.ai.promptTemplateVersions.get(featureCategory, isSystemPrompt)}`),
      create: ({ typhonDomain, isSystemPrompt, payload }) => fetchTyphon(`${typhonDomain}${typhonRoutes.ai.promptTemplateVersions.create(isSystemPrompt)}`, {
        method: 'PUT',
        body: payload,
      }),
      activate: ({
        typhonDomain,
        featureCategory,
        publicVersion,
        isSystemPrompt,
      }) => fetchTyphon(`${typhonDomain}${typhonRoutes.ai.promptTemplateVersions.activate(
        featureCategory,
        publicVersion,
        isSystemPrompt,
      )}`, {
        method: 'PUT',
      }),
    },
    customPromptContexts: {
      get: ({ typhonDomain }) => fetchTyphon(`${typhonDomain}${typhonRoutes.ai.customPromptContexts.get}`),
      upsert: ({ typhonDomain, payload }) => fetchTyphon(`${typhonDomain}${typhonRoutes.ai.customPromptContexts.upsert}`, {
        method: 'POST',
        body: payload,
      }),
      activate: ({ typhonDomain, publicVersion, payload }) => fetchTyphon(`${typhonDomain}${typhonRoutes.ai.customPromptContexts.activate(publicVersion)}`, {
        method: 'PUT',
        body: payload,
      }),
      archive: ({ typhonDomain, payload }) => fetchTyphon(`${typhonDomain}${typhonRoutes.ai.customPromptContexts.archive}`, {
        method: 'POST',
        body: payload,
      }),
      unarchive: ({ typhonDomain, payload }) => fetchTyphon(`${typhonDomain}${typhonRoutes.ai.customPromptContexts.unarchive}`, {
        method: 'POST',
        body: payload,
      }),
    },
    agent: {
      invoke: ({ typhonDomain, body }) => fetchTyphon(`${typhonDomain}/ai/agent/invoke`, {
        method: 'POST',
        body,
        hasSilentAlertMessage: true,
      }),
      /**
       * POST /ai/agent/invoke — returns { status, body } without throwing on 4xx/5xx
       * so callers can read full JSON (e.g. text + llmSessionUuid on errors).
       */
      invokeWithStatus: async ({ typhonDomain, body }) => {
        const response = await typhonAjaxFetch(`${typhonDomain}/ai/agent/invoke`, {
          method: 'POST',
          body: JSON.stringify(body),
          hasSilentAlertMessage: true,
          returnResponse: true,
        });
        let parsedBody = null;
        try {
          parsedBody = await response.json();
        } catch {
          parsedBody = null;
        }
        return { status: response.status, body: parsedBody };
      },
    },
    llm: {
      getTranscript: ({ typhonDomain, llmSessionUuid }) => fetchTyphon(
        `${typhonDomain}/ai/llm/${llmSessionUuid}/transcript`,
        { hasSilentAlertMessage: true },
      ),
    },
    tools: {
      getAll: ({ typhonDomain }) => fetchTyphon(`${typhonDomain}/ai/tools`),
    },
  },
  journeyAiAgentConfigs: {
    get: ({ typhonDomain }) => fetchTyphon(`${typhonDomain}${typhonRoutes.journeyAiAgentConfigs.get}`),
    create: ({ typhonDomain, payload }) => fetchTyphon(`${typhonDomain}${typhonRoutes.journeyAiAgentConfigs.create}`, {
      method: 'POST',
      body: {
        journey_token: payload.journeyToken,
        journey_node_public_id: payload.journeyNodePublicId,
        action_type_slug: payload.actionTypeSlug,
      },
    }),
    delete: ({ typhonDomain, payload }) => fetchTyphon(`${typhonDomain}${typhonRoutes.journeyAiAgentConfigs.delete(payload.journeyToken, payload.journeyNodePublicId)}`, {
      method: 'DELETE',
    }),
  },
  applicationReview: {
    escalate: ({
      journeyApplicationToken,
      journeyToken,
      payload,
      typhonDomain,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.applications.escalate({ journeyApplicationToken, journeyToken })}`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    ),
    archiveApplication: ({
      journeyApplicationToken,
      journeyToken,
      typhonDomain,
      payload,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.applications.archiveApplication({ journeyApplicationToken, journeyToken })}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
        json: false,
      },
    ),
    getApplication: ({ journeyApplicationToken, journeyToken, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.applications.getApplication({ journeyApplicationToken, journeyToken })}`,
    ),
    getJourneyApplicationAssignableAgents: ({
      checkAssignPermissions = true,
      typhonDomain,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.applications.getJourneyApplicationAssignableAgents({ checkAssignPermissions })}`,
    ),
    getJourneyApplicationAssignableAgentsWithPermissions: ({
      checkAssignPermissions = true,
      journeyApplicationTokens,
      journeyTokens,
      typhonDomain,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.applications.getJourneyApplicationAssignableAgentsWithPermissions({
        checkAssignPermissions,
      })}`,
      {
        method: 'POST',
        body: JSON.stringify({
          journeyApplicationTokens,
          journeyTokens,
        }),
      },
    ),
    getJourneyApplicationAssignedAgents: ({
      journeyApplicationToken,
      journeyToken,
      typhonDomain,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.applications.getJourneyApplicationAssignedAgents({ journeyApplicationToken, journeyToken })}`,
    ),
    assignJourneyApplication: ({
      agentToken,
      typhonDomain,
      journeyApplicationTokens,
      reassign = false,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.applications.assignJourneyApplicationAgents}`, {
        body: JSON.stringify({
          agent_token: agentToken,
          journey_application_tokens: journeyApplicationTokens,
          reassign,
        }),
        method: 'POST',
        json: false,
        silent: true,
      }),
    unassignJourneyApplication: ({
      agentTokens,
      journeyApplicationToken,
      journeyToken,
      typhonDomain,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.applications.unassignJourneyApplicationAgents(
        { journeyApplicationToken, journeyToken },
      )}`, {
        body: JSON.stringify({ agent_tokens: agentTokens }),
        method: 'PUT',
        json: false,
        silent: true,
      }),
    getApplications: ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.applications.getApplications}${queryParamsStr}`,
    ),
    getApplicationsDaffy: ({ queryParamsStr }) => fetchDaffy(
      `${routes.journey_applications.search}${queryParamsStr}`,
    ),
    rerunApplication: ({
      entities,
      entityTokensToRemove,
      journeyApplicationToken,
      journeyToken,
      shouldRefreshCache = false,
      typhonDomain,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.applications.rerunApplication({ journeyApplicationToken, journeyToken })}`,
      {
        method: 'POST',
        body: JSON.stringify({ entities, exclude_entity_tokens: entityTokensToRemove }),
        headers: {
          ...shouldRefreshCache && {
            'alloy-refresh-cache': shouldRefreshCache,
          },
        },
      },
    ),
    reviewJourneyApplication: ({
      typhonDomain,
      review,
      journeyApplicationToken,
      journeyToken,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.applications.reviewJourneyApplication({ journeyApplicationToken, journeyToken })}`,
      {
        method: 'POST',
        body: JSON.stringify(review),
      },
    ),
    getApplicationQueueAgentQuery: ({ typhonDomain, queueType }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.journeys.applications.applicationQueueAgentQueries}?queueType=${queueType}`),
    updateApplicationQueueAgentQuery: ({
      typhonDomain,
      filterValuesByColumn,
      searchValuesByColumn,
      queueType,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.applications.applicationQueueAgentQueries}`,
      {
        method: 'PUT',
        body: JSON.stringify({ filterValuesByColumn, searchValuesByColumn, queueType }),
      },
    ),
    startApplicationQueueExportCSV: ({
      typhonDomain, queryParamsStr,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.applications.startApplicationQueueExport('csv')}${queryParamsStr}`,
      {
        method: 'POST',
      }),
    getApplicationActivity: ({
      typhonDomain,
      journeyToken,
      journeyApplicationToken,
    }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.journeys.applications.getApplicationActivity({
      journeyToken,
      journeyApplicationToken,
    })}`),
  },

  cases: {
    getCaseWorks: async ({ caseToken, typhonDomain }) => fetchTyphon(
      `${typhonDomain}${typhonRoutes.cases.work(caseToken)}`,
    ),

    createCaseWork: ({
      caseToken,
      closed,
      note,
      reasons,
      status,
      typhonDomain,
    }) => fetchTyphon(`${typhonDomain}${typhonRoutes.cases.work(caseToken)}`, {
      body: {
        closed: closed !== undefined ? closed : status === 'Close' || undefined,
        note,
        reasons,
        status,
      },
      method: 'POST',
    }),
  },
  cloning: {
    /**
      *
      * @typedef {Object} WorkflowCloneResult
      * @property {'workflow'} clone_type discriminating field (for future cloning types)
      * @property {string} workflow_token
      *
      * @typedef {WorkflowCloneResult} CloneResult
      *
      * @typedef {Object} CloneResponse
      * @property {string} message
      * @property {string} redirect_url
      * @property {string} cloningToken
      * @property {CloneResult} cloning_result
      * @returns {Promise<CloneResponse>} result
      */
    clone: async ({
      agentId,
      cloningType,
      isPolicyTokenImport = false,
      policyToken,
      targetOrganizationSlug,
      typhonDomain,
    }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.cloning.clone}`, {
      method: 'POST',
      body: JSON.stringify({
        agent_id: agentId,
        policy_token: policyToken,
        cloning_type: cloningType,
        target_organization_slug: targetOrganizationSlug,
        is_policy_token_import: isPolicyTokenImport,
      }),
    }),
    /**
     * @param {Object} args
     * @param {string} args.applicationToken
     * @param {boolean} [args.backtesting]
     * @param {string} [args.cloningType]
     * @param {boolean} [args.includeSandboxPersonas]
     * @param {string} args.typhonDomain
     * @param {number} args.versionId
     * @param {Record<string, string>} [args.workflowsToRename]
     *
     * @typedef {Object} SchemaResponse
     * @property {number} status_code
     * @property {string | null} error
     * @property {string} policy_token
     * @returns {Promise<SchemaResponse>} response
     */
    schema: async ({
      applicationToken,
      backtesting = false,
      cloningType = 'workflow',
      includeSandboxPersonas = false,
      typhonDomain,
      versionId,
      workflowsToRename,
    }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.cloning.schema}`, {
      method: 'POST',
      body: JSON.stringify({
        application_token: applicationToken,
        version_id: versionId,
        cloning_type: cloningType,
        workflows_to_rename: workflowsToRename,
        include_sandbox_personas: includeSandboxPersonas,
        backtesting,
      }),
    }),
  },

  customers: {
    switchCustomerContext: customerId => ajaxFetch(`${routes.admin.customers.switchCustomerContext(customerId)}`, { method: 'GET', json: false }),
    checkAccess: organizationNameSlug => ajaxFetch(`${routes.v3.admin.checkAccess}?organization_name_slug=${organizationNameSlug}`, 'GET'),
  },

  dataExports: {
    getDataExportDetails: ({ typhonDomain, exportToken }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.dataExports.getDetails(exportToken)}`, {
      returnResponse: true,
    }),
    downloadDataExport: ({ typhonDomain, exportToken }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.dataExports.download(exportToken)}`, {
      returnResponse: true,
    }),
  },

  documents: {
    getDocumentTypes: ({ typhonDomain }) => fetchTyphon(`${typhonDomain}${typhonRoutes.documents.documentTypes.get}`),
  },

  embeddedFinance: {
    cloneJourney: ({ typhonDomain, journeyToken, childCustomerSlugs }) => fetchTyphon(`${typhonDomain}${typhonRoutes.embeddedFinance.cloneJourney({ journeyToken })}`, {
      body: {
        child_customer_slugs: childCustomerSlugs,
      },
      method: 'POST',
    }),
    // TODO: deprecate PLATFORM-904 PLATFORM-905
    getChildCustomers: () => fetchDaffy(routes.v3.admin.afef_child_customers),
    getChildCustomersOfAfefParent: () => fetchDaffy(routes.v3.afef.childCustomers),
    getAllJourneyFederationStatus: ({ typhonDomain }) => fetchTyphon(
      `${typhonDomain}${typhonRoutes.embeddedFinance.getAllJourneyFederationStatus()}`,
    ),
    getJourneyFederationStatus: ({ typhonDomain, journeyToken }) => fetchTyphon(
      `${typhonDomain}${typhonRoutes.embeddedFinance.getJourneyFederationStatus({ journeyToken })}`,
    ),
    getWorkflowFederationStatus: ({ typhonDomain, workflowToken }) => fetchTyphon(
      `${typhonDomain}${typhonRoutes.embeddedFinance.getWorkflowFederationStatus({ workflowToken })}`,
    ),
  },

  entities: {
    getCustomerListEntitiesColumns: ({ typhonDomain }) => fetchTyphon(
      `${typhonDomain}${typhonRoutes.entities.customerListEntitiesColumns}`,
    ),

    documents: {
      getDocuments: ({ entityToken, typhonDomain }) => fetchTyphon(
        `${typhonDomain}${typhonRoutes.entities.documents.get(entityToken)}`,
      ),

      renameDocument: ({
        documentToken, entityToken, name, typhonDomain,
      }) => typhonAjaxFetch(
        `${typhonDomain}/documents/${documentToken}/name`,
        {
          method: 'PATCH',
          headers: { 'Alloy-Entity-Token': entityToken },
          body: JSON.stringify({ name }),
        },
      ),

      updateDocument: ({
        approved,
        alternativeOutcome,
        documentToken,
        entityToken,
        name,
        type,
        typhonDomain,
      }) => fetchTyphon(`${typhonDomain}${typhonRoutes.entities.documents.single({ documentToken, entityToken })}`, {
        body: {
          approved,
          alternative_outcome: alternativeOutcome,
          name,
          type,
        },
        method: 'PATCH',
      }),
    },

    getEntity: ({ entityToken }) => fetchDaffy(`${routes.entities.detailData(entityToken)}?json=true`),
    getEntityGroup: ({ typhonDomain, entityToken, includeArchived = false }) => {
      const base = `${typhonDomain}${typhonRoutes.entities.getEntityGroupByNameTokenOrId({ entityToken })}`;
      return fetchTyphon(includeArchived ? `${base}?include_archived_relationships=true` : base);
    },
    getEntityByNameTokenOrId: ({
      entityName, entityToken, externalEntityId, typhonDomain,
    }) => fetchTyphon(
      `${typhonDomain}${typhonRoutes.entities.getEntityByNameTokenOrId({ entityName, entityToken, externalEntityId })}`,
    ),

    getEvaluation: ({
      entityToken,
      evaluationToken,
    }) => fetchDaffy(routes.entities.evaluation.detail({ entityToken, evaluationToken })),

    getEntityPii: ({ typhonDomain, entityToken, before = null }) => fetchTyphon(
      `${typhonDomain}${typhonRoutes.entities.pii(entityToken)}${before ? `?before=${before}` : ''}`,
    ),

    getEntityFraudSnapshot: ({ typhonDomain, entityToken }) => fetchTyphon(`${typhonDomain}${typhonRoutes.entities.entityFraudSnapshot(entityToken)}`),

    getEntityFraudSnapshotByEvaluation: ({ typhonDomain, evaluationToken }) => fetchTyphon(
      `${typhonDomain}${typhonRoutes.entities.entityFraudSnapshotByEvaluation(evaluationToken)}`,
    ),

    getEntityFraudSnapshotHistory: ({ typhonDomain, entityToken, days }) => {
      const url = `${typhonDomain}${typhonRoutes.entities.entityFraudSnapshotHistory(entityToken)}`;
      return fetchTyphon(days !== undefined ? `${url}?days=${days}` : url);
    },

    getPublishedAttributes: ({ typhonDomain, entityToken }) => fetchTyphon(`${typhonDomain}${typhonRoutes.entities.publishedAttributes(entityToken)}`),

    getPublishedAttributesData: ({ typhonDomain, entityToken }) => fetchTyphon(`${typhonDomain}${typhonRoutes.entities.publishedAttributesData(entityToken)}`),

    getEntityTimeline: ({ typhonDomain, entityToken }) => fetchTyphon(`${typhonDomain}${typhonRoutes.entities.timeline(entityToken)}`),

    getEntityPDFExport: ({ typhonDomain, entityToken }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.entities.pdfExport(entityToken)}`,
      { returnResponse: true },
    ),

    getActionThresholdResults: ({
      typhonDomain, entityToken, evaluationToken, isPortfolioEvaluation, dimensions,
    }) => fetchTyphon(
      `${typhonDomain}${typhonRoutes.entities.actionThresholdResults({
        entityToken,
        evaluationToken,
        queryParams: {
          is_portfolio_evaluation: isPortfolioEvaluation,
          ...(dimensions?.length > 0 && { dimensions }),
        },
      })}`,
    ),

    getActionEvidence: ({
      typhonDomain, entityToken, evaluationToken, actionId, isPortfolioEvaluation, dimensions,
    }) => fetchTyphon(
      `${typhonDomain}${typhonRoutes.entities.actionEvidences({
        entityToken,
        evaluationToken,
        actionId,
        queryParams: {
          limit: 1000,
          offset: 0,
          is_portfolio_evaluation: isPortfolioEvaluation,
          ...(dimensions?.length > 0 && { dimensions }),
        },
      })}`,
    ),

    exportThresholdEvidenceCsv: ({
      typhonDomain,
      entityToken,
      evaluationToken,
      actionId,
      thresholdId,
      isPortfolioEvaluation,
      dimensions,
    }) => fetchTyphon(
      `${typhonDomain}${typhonRoutes.entities.thresholdEvidenceExportCsv({
        entityToken,
        evaluationToken,
        actionId,
        thresholdId,
        queryParams: {
          limit: 1000,
          offset: 0,
          is_portfolio_evaluation: isPortfolioEvaluation,
          ...(dimensions?.length > 0 && { dimensions }),
        },
      })}`,
    ),

    getEntityPortfolioEvaluations: ({
      typhonDomain,
      entityToken,
      queryParamsStr,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.entities.getEntityPortfolioEvaluations(entityToken)}${queryParamsStr}`,
    ),
  },

  evaluations: {
    creditAll: async ({ entityToken, evaluationToken, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.entities.credit({ entityToken, evaluationToken })}`,
    ),
    llmSummary: async ({ entityToken, evaluationToken, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.entities.llmSummary({ entityToken, evaluationToken })}`,
      { method: 'POST' },
    ),
  },

  events: {
    getEventRequest: async ({ eventRequestToken, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.events.get(eventRequestToken)}`,
    ),
    getEventTypeStatuses: async ({ eventType, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.settings.events.eventStatuses(eventType)}`,
    ),
    createEventStatus: async ({ eventType, typhonDomain, body }) => fetchTyphon(
      `${typhonDomain}${typhonRoutes.settings.events.eventStatuses(eventType)}`,
      {
        body,
        method: 'POST',
      },
    ),
    updateEventStatuses: async ({ eventType, typhonDomain, body }) => fetchTyphon(
      `${typhonDomain}${typhonRoutes.settings.events.eventStatuses(eventType)}`,
      {
        body,
        method: 'PATCH',
      },
    ),
    bulkCreateMappings: async ({ typhonDomain, body }) => fetchTyphon(
      `${typhonDomain}${typhonRoutes.settings.events.bulkCreateMappings}`,
      {
        body,
        method: 'POST',
      },
    ),
    getEvents: async ({ typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.settings.events.events}`,
    ),
    getWorkflowEligibleEvents: async ({ typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.settings.events.workflowEligibleEvents}`,
    ),
    patchEventStatus: async ({
      eventType, eventStatusToken, typhonDomain, body,
    }) => fetchTyphon(
      `${typhonDomain}${typhonRoutes.settings.events.patchEventStatus(eventType, eventStatusToken)}`,
      {
        body,
        method: 'PATCH',
      },
    ),
    getEventLogs: async ({ typhonDomain, queryParam }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.settings.events.getEventLogs(queryParam)}`,
    ),
    getBatchLogs: async ({ typhonDomain, queryParam }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.settings.events.getBatchLogs(queryParam)}`,
    ),
  },

  getUserContext: () => fetchDaffy(routes.v3.userContext),

  journeyActions: {
    getJourneyActions: ({
      typhonDomain,
      activeOnly = true,
      customReviewOnly = false,
      includeArchived = false,
      parentReviewOnly = false,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.actions.getActions({
        activeOnly,
        customReviewOnly,
        includeArchived,
        parentReviewOnly,
      })}`,
    ),
    archiveJourneyActionVersion: ({ actionId, version, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.actions.archiveActionVersion(actionId, version)}`,
      { method: 'PUT', json: false },
    ),
    archiveSdkKeys: ({ journeyAssociatedKeys, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.settings.sdk.archiveKey}`, {
        method: 'PUT',
        json: false,
        body: journeyAssociatedKeys,
      },
    ),
    getJourneyAssociatedKeys: ({ journeyToken, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.settings.sdk.journeyAssociatedKeys(journeyToken)}`,
      { method: 'GET' },
    ),
    createJourneyAction: ({
      actionName,
      actionType,
      eventTypeTitle,
      expSeconds,
      outcomes,
      active,
      actionId,
      typhonDomain,
      aiActionTypeSlug,
      autoAssignRoles,
    }) => fetchTyphon(`${typhonDomain}${typhonRoutes.journeys.actions.createActionVersion(actionId)}`, {
      body: {
        name: actionName,
        type: actionType.toLowerCase(),
        eventTypeTitle,
        expSeconds,
        outcomes,
        active,
        aiActionTypeSlug,
        ...(autoAssignRoles ? { autoAssignRoles } : {}),
      },
      method: 'POST',
    }),
    cloneJourneyAction: ({
      actionName,
      actionType,
      expSeconds,
      outcomes,
      active,
      actionId,
      targetCustomerId,
      eventTypeTitle,
      typhonDomain,
    }) => fetchTyphon(`${typhonDomain}${typhonRoutes.journeys.actions.cloneActionVersion(actionId)}`, {
      body: {
        name: actionName,
        type: actionType.toLowerCase(),
        expSeconds,
        outcomes,
        active,
        targetCustomerId,
        eventTypeTitle,
      },
      method: 'POST',
    }),
  },

  journeys: {
    getJourney: ({ typhonDomain, journeyToken }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.get(journeyToken)}`,
    ),
    getJourneys: ({ typhonDomain, filter = null, isEmbeddedFinance = false }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.create}${filter ? buildUrlParamsString({
        filter,
        ...(isEmbeddedFinance ? { embeddedFinance: isEmbeddedFinance } : {}),
      }, true) : ''}`,
    ),
    getJourneysWithPagination: ({
      typhonDomain,
      pageNum = 1,
      numRowsPerPage = 20,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.getJourneysWithPagination}${buildUrlParamsString({
        pageNum,
        numRowsPerPage,
        orderBy: JSON.stringify([['updated_at', 'DESC']]),
      })}`,
    ),
    /**
     * @typedef {Object} GetJourneyVersionsParams
     * @property {string} status - What status versions returned should have
     */
    /**
     * @typedef {Object} GetJourneyVersionsArgs
     * @property {string} typhonDomain
     * @property {string} journeyToken
     * @property {GetJourneyVersionsParams} [queryParams]
     */
    getJourneyVersions: (/** @type GetJourneyVersionsArgs */{
      typhonDomain, journeyToken, queryParams,
    }) => {
      let url = `${typhonDomain}${typhonRoutes.journeys.getVersions(journeyToken)}`;
      if (queryParams) {
        const params = new URLSearchParams(queryParams).toString();
        url = `${url}?${params}`;
      }
      return typhonAjaxFetch(url);
    },
    getJourneyVersionDetail: ({ typhonDomain, journeyToken, journeyVersion }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.getVersion(journeyToken, journeyVersion)}`,
    ),
    getJourneyVersionActions: ({ typhonDomain, journeyToken, publicVersion }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.getJourneyVersionActions({ journeyToken, publicVersion })}`,
    ),
    getJourneySchema: ({ typhonDomain, journeyToken, versionId = null }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.getSchema(journeyToken)}`, {
        ...(versionId && {
          headers: {
            'alloy-journey-version': versionId,
          },
        }),
      },
    ),
    getJourneyVersionServices: ({
      journeyToken,
      typhonDomain,
      versionNumbers,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.getJourneyVersionServices({
        journeyToken,
        queryParams: versionNumbers
          .map(versionNumber => `versions=${versionNumber}`)
          .join('&'),
      })}`,
    ),
    createJourney: ({
      body,
      typhonDomain,
      journeyToken,
      isSandBox,
      alloyAuditAccountSlugs,
    }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.journeys.createJourney(journeyToken)}`, {
      body: JSON.stringify(body),
      method: 'POST',
      headers: {
        'Alloy-Sandbox': isSandBox,
        ...(alloyAuditAccountSlugs ? { 'Alloy-Audit-Account-Slugs': alloyAuditAccountSlugs } : {}),
      },
      silent: true,
    }),
    checkJourneyNameForConflict: ({
      queryParams,
      typhonDomain,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.checkJourneyNameForConflict(queryParams)}`,
    ),
    checkWorkflowNamesForConflicts: ({
      typhonDomain, payload,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.checkWorkflowNamesForConflicts}`,
      {
        body: JSON.stringify(payload),
        method: 'POST',
      },
    ),
    championChallenger: {
      createJourneyChampionChallenger: async ({
        typhonDomain,
        journeyToken,
        payload,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.journeys.championChallenger(journeyToken)}`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
          silent: true,
        }),
      getJourneyChampionChallenger: async ({
        typhonDomain,
        journeyToken,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.journeys.championChallenger(journeyToken)}`,
        { returnResponse: true },
      ),
      getJourneyChampionChallengers: async ({
        typhonDomain,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.journeys.championChallengers}`,
      ),
      getJourneyChampionChallengersHistory: async ({
        typhonDomain,
        journeyToken,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.journeys.championChallengersHistory(journeyToken)}`,
      ),
      endChampionChallenger: async ({
        typhonDomain,
        journeyToken,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.journeys.championChallenger(journeyToken)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            status: 'ended',
          }),
        },
      ),
    },
    getChildEntityPayloadsByEntityToken: ({
      journeyApplicationToken,
      journeyToken,
      typhonDomain,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.childPayload.getChildEntityPayloadsByEntityToken({ journeyToken, journeyApplicationToken })}`,
    ),
    updateJourneySettings: async ({
      journeyToken,
      typhonDomain,
      secondsToExpiration,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.settings(journeyToken)}`,
      {
        body: JSON.stringify({
          seconds_to_expiration: secondsToExpiration,
        }),
        json: true,
        method: 'POST',
      },
    ),

    alerts: {
      getManualReview: async ({
        alertManualReviewToken,
        journeyToken,
        journeyApplicationToken,
        typhonDomain,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.journeys.alerts.getManualReview({
          alertManualReviewToken,
          journeyToken,
          journeyApplicationToken,
        })}`,
      ),
    },

    notes: {
      getJourneyApplicationNotes: async ({
        journeyToken,
        journeyApplicationToken,
        typhonDomain,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.journeys.notes.getJourneyApplicationNotes({
          journeyToken,
          journeyApplicationToken,
        })}`,
      ),
    },
  },

  notes: {
    postNote: body => fetchDaffy(routes.notes, { body, method: 'POST' }),
    createNote: ({ typhonDomain, body }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.notes.createNote}`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    ),
  },

  portfolioEvaluations: {
    evaluationDetails: async ({
      portfolioEvaluationToken,
      page,
      paginateBy,
      q,
      typhonDomain,
    }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.portfolioEvaluations.evaluationDetails({
      portfolioEvaluationToken,
      page,
      paginateBy,
      q: typeof q === 'string' ? q : JSON.stringify(q),
    })}`),

    entityDetail: async ({
      dimensions,
      entityToken,
      portfolioEvaluationToken,
      typhonDomain,
    }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.portfolioEvaluations.entityDetail({
      portfolioEvaluationToken,
      entityToken: entityToken.trim(),
      dimensions: dimensions && JSON.stringify(dimensions),
    })}`),
  },

  journeyTests: {
    runSandboxPersonasTest: ({
      typhonDomain,
      journeyToken,
      publicVersion,
      payload,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeyTests.sandboxPersonas.runTest(journeyToken, publicVersion)}`,
      {
        body: JSON.stringify({ payload }),
        method: 'POST',
      },
    ),
    getSandboxPersonaTestProgress: ({
      typhonDomain,
      testToken,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeyTests.sandboxPersonas.getProgress(testToken)}`,
    ),
    getJourneyTestHistory: ({
      typhonDomain,
      journeyToken,
      queryParamsStr,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeyTests.getHistory(journeyToken)}?${queryParamsStr}`,
    ),

    shadowTest: {
      create: ({
        endDate,
        journeyToken,
        sampleSize,
        startDate,
        typhonDomain,
        versionNumbers,
      }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.journeyTests.shadowTest.create}`, {
        body: JSON.stringify({
          journey_token: journeyToken,
          config_sample_size: sampleSize,
          config_start_date: startDate,
          config_end_date: endDate,
          versions: versionNumbers,
        }),
        method: 'POST',
      }),

      get: ({
        shadowTestToken,
        typhonDomain,
      }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.journeyTests.shadowTest.get(shadowTestToken)}`),

      getActiveTest: ({
        journeyToken,
        typhonDomain,
      }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.journeyTests.shadowTest.getActiveTest(journeyToken)}`, {
        returnResponse: true,
      }),

      end: ({
        typhonDomain,
        shadowTestToken,
      }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.journeyTests.shadowTest.end(shadowTestToken)}`, {
        json: false,
        method: 'PUT',
      }),
    },

    multiEnvironmentConfig: {
      create: ({
        multiEnvironmentConfig,
        typhonDomain,
      }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.journeyTests.multiEnvironmentConfig.create}`, {
        body: JSON.stringify(multiEnvironmentConfig),
        method: 'POST',
      }),
      get: ({
        typhonDomain,
        multiEnvironmentConfigToken,
      }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.journeyTests.multiEnvironmentConfig.get(multiEnvironmentConfigToken)}`),
    },
  },

  journeyAnalytics: {
    getJourneyApplicationBranchNames: ({ typhonDomain, journeyToken }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeys.getBranchNames(journeyToken)}`,
    ),
    getJourneyApplicationDataSources: ({ typhonDomain, queryParamsStr }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeyAnalytics.journeyApplicationDataSources}${queryParamsStr}`,
    ),
    getNumAppsPerOutcome: ({ typhonDomain, queryParamsStr }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeyAnalytics.journeyOutcomes}${queryParamsStr}`,
    ),
    getNumAppsPerOpenStatus: ({ typhonDomain, queryParamsStr }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeyAnalytics.openJourneyApplicationStatuses}${queryParamsStr}`,
    ),
    getManulReviewApplicationOutcomes: ({ typhonDomain, queryParamsStr }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeyAnalytics.manualReviewOutcomes}${queryParamsStr}`,
    ),
    getNumAppsPerOutcomePerDay: ({ typhonDomain, queryParamsStr, timezone }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeyAnalytics.journeyOutcomesTimeSeries}${queryParamsStr}&timezone=${timezone}`,
      {
        cache: 'no-store',
      },
    ),
    getJourneyApplicationTotals: ({ typhonDomain, queryParamsStr }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeyAnalytics.journeyApplicationTotals}${queryParamsStr}`,
    ),
    getJourneyApplicationTotalsTimeSeries: ({
      typhonDomain,
      queryParamsStr,
      timezone,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeyAnalytics.journeyApplicationTotalsTimeSeries}${queryParamsStr}&timezone=${timezone}`,
    ),
    getJourneyApplicationTotalReviews: ({ typhonDomain, queryParamsStr }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeyAnalytics.journeyApplicationTotalReviews}${queryParamsStr}`,
    ),
    getJourneyStepOutcomeDetails: ({ typhonDomain, queryParamsStr }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.journeyAnalytics.journeyStepOutcomeDetails}${queryParamsStr}`,
    ),

    workflowAttributes: {
      getOutcomesOverTime: ({
        typhonDomain,
        queryParamsStr,
        workflowToken,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.journeyAnalytics.workflowAttributes.outcomesOverTime(workflowToken)}${queryParamsStr}`,
      ),
      getReasonCodes: ({
        typhonDomain,
        queryParamsStr,
        workflowToken,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.journeyAnalytics.workflowAttributes.reasonCodes(workflowToken)}${queryParamsStr}`,
      ),
      getReviewedEvalOutcomes: ({
        typhonDomain,
        queryParamsStr,
        workflowToken,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.journeyAnalytics.workflowAttributes.reviewedEvalOutcomes(workflowToken)}${queryParamsStr}`,
      ),
      getReviewReasons: ({
        typhonDomain,
        queryParamsStr,
        workflowToken,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.journeyAnalytics.workflowAttributes.reviewReasons(workflowToken)}${queryParamsStr}`,
      ),
      getTagsTriggered: ({
        typhonDomain,
        queryParamsStr,
        workflowToken,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.journeyAnalytics.workflowAttributes.tagsTriggered(workflowToken)}${queryParamsStr}`,
      ),
      getTotalEvalsOverTime: ({
        typhonDomain,
        queryParamsStr,
        workflowToken,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.journeyAnalytics.workflowAttributes.totalEvalsOverTime(workflowToken)}${queryParamsStr}`,
      ),
    },
  },

  caseManagementAnalytics: {
    getCaseStatuses: ({ typhonDomain, queryParamsStr }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.caseManagementAnalytics.caseStatuses}${queryParamsStr}`,
    ),
    getDailyCasesCreated: ({ typhonDomain, queryParamsStr }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.caseManagementAnalytics.dailyCasesCreated}${queryParamsStr}`,
    ),
    getTagsClosedCaseReasons: ({ typhonDomain, queryParamsStr }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.caseManagementAnalytics.tagsClosedCaseReasons}${queryParamsStr}`,
    ),
    getTagsTriggeredTimeSeries: ({ typhonDomain, queryParamsStr }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.caseManagementAnalytics.tagsTriggeredTimeSeries}${queryParamsStr}`,
    ),
    getTagsTriggered: ({ typhonDomain, queryParamsStr }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.caseManagementAnalytics.tagsTriggered}${queryParamsStr}`,
    ),
    getCaseClosedReasonsTimeSeries: ({ typhonDomain, queryParamsStr }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.caseManagementAnalytics.caseClosedReasonsTimeSeries}${queryParamsStr}`,
    ),
  },

  ruleNotificationPolicies: {
    archive: ({ typhonDomain, token }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.ruleNotificationPolicies.create}/${token}`,
      { method: 'PATCH' },
    ),
    create: ({ typhonDomain, policy }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.ruleNotificationPolicies.create}`,
      { body: JSON.stringify({ policy }), method: 'POST' },
    ),
    update: ({ typhonDomain, policy, token }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.ruleNotificationPolicies.create}/${token}`,
      { body: JSON.stringify({ policy }), method: 'PUT' },
    ),
    getAll: ({ typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.ruleNotificationPolicies.get}`,
    ),
  },
  queue: {
    createReviewInsightsExport: ({
      tableParams,
    }) => ajaxFetch(`${routes.queues.data}${tableParams}&download`),

    getReasonCodeOptions: () => ajaxFetch(routes.queues.reasonCodeOptions),
  },

  settings: {
    getOutcomes: async ({ typhonDomain = '', options = {} }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.settings.outcomes.getOutcomes(options)}`),
    getReviewReasons: async () => (await fetchDaffy(routes.settings.reasons)).reasons,
    getRoles: async ({ params }) => get(
      (await fetchDaffy(`${routes.settings.roles.list}${buildUrlParamsString(params, true)}`)), 'data[0].data', [],
    ),
    createTag: async ({ tag }) => ajaxFetch(routes.settings.tags, {
      body: { tagData: tag },
      headers: { 'Content-type': 'application/json' },
      method: 'POST',
      requestBodySnakeCase: true,
    }),
    createOutcome: async ({ typhonDomain = '', outcome }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.settings.outcomes.createOutcome}`, {
      body: JSON.stringify(outcome),
      method: 'POST',
    }),
    updateOutcome: async ({
      typhonDomain = '', encodedOutcome, outcomeData,
    }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.settings.outcomes.updateOutcome(encodedOutcome)}`, {
      body: JSON.stringify({ ...outcomeData }),
      method: 'PUT',
    }),
    archiveOutcome: async ({ typhonDomain, encodedOutcome, customerOutcomeId }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.settings.outcomes.archiveOutcome(encodedOutcome)}`, {
      body: JSON.stringify({ customer_outcome_id: customerOutcomeId }),
      method: 'PATCH',
    }),
    unarchiveOutcome: async ({ typhonDomain, encodedOutcome, customerOutcomeId }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.settings.outcomes.unarchiveOutcome(encodedOutcome)}`, {
      body: JSON.stringify({ customer_outcome_id: customerOutcomeId }),
      method: 'PATCH',
    }),
    deleteOutcome: async ({ typhonDomain, encodedOutcome, customerOutcomeId }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.settings.outcomes.deleteOutcome({ encodedOutcome, customerOutcomeId })}`, {
        method: 'DELETE',
      },
    ),
    getTags: async ({ typhonDomain }) => (typhonAjaxFetch(`${typhonDomain}${typhonRoutes.settings.tags}`)),
    getCustomerPartners: async ({ typhonDomain }) => (typhonAjaxFetch(`${typhonDomain}${typhonRoutes.settings.customerPartners}`)),
    roles: {
      permissions: async customerRoleId => get(
        (await fetchDaffy(
          `${routes.settings.roles.list}${customerRoleId}${buildUrlParamsString({ customer_role_id: customerRoleId, json: true })}`,
        )), 'data.permissions', {},
      ),
    },
    getEnabledAndPreviouslyUsedServices: ({ typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.settings.services}`,
      {
        headers: { 'alloy-enabled-and-previously-used-services': true },
      },
    ),
    getSandboxAndProductionServices: ({ typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.settings.services}`,
      {
        headers: { 'alloy-include-sandbox-services': true },
      },
    ),
    getReasonCodes: async ({ typhonDomain }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.settings.reasonCodes}`),
    getTriggeredReasonCodes: async ({ typhonDomain }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.settings.triggeredReasonCodes}`),
    getFraudScores: async ({ typhonDomain }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.settings.fraudScores}`),
    getAttributes: async ({ typhonDomain }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.settings.attributes}`),
    getServices: async ({ typhonDomain }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.settings.services}`),
    updateTag: async ({ tag }) => ajaxFetch(routes.settings.tags, {
      body: JSON.stringify({
        tag_data: tag,
        tag_type_names: tag.associated_types ?? [],
      }),
      headers: { 'Content-Type': 'application/json' },
      method: 'PUT',
    }),
    getSavedSearches: async ({
      filterBy = {},
      searchBy = {},
      page = 0,
    }) => {
      const paramsString = buildUrlParamsString({
        filterBy,
        searchBy,
        paginateBy: 100,
        page,
        orderBy: ['name', 'ASC'],
        json: true,
      }, true);
      return ajaxFetch(`${routes.settings.queries.data}${paramsString}`, {
        headers: { 'Content-Type': 'application/json' },
      });
    },
    getServiceConfig: async ({
      typhonDomain, serviceId,
    }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.settings.serviceConfigOption(serviceId)}`),
    getAccountServiceConfigs: async ({ typhonDomain }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.settings.accountServiceConfigs}`),
    getUniversalAttributes: async ({ typhonDomain }) => {
      try {
        return await typhonAjaxFetch(
          `${typhonDomain}${typhonRoutes.settings.universalAttributes}`,
          { hasSilentAlertMessage: true },
        );
      } catch {
        // TODO - MAPLE-33 - remove once FF is on for everyone
        return { universal_attributes: [] };
      }
    },
  },
  sandboxPersonas: {
    getSandboxPersonas: async ({ typhonDomain }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.sandboxPersonas.list}`),
    postSandboxPersona: ({
      body,
      typhonDomain,
    }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.sandboxPersonas.post}`, {
      body: JSON.stringify(body),
      method: 'POST',
    }),
    putSandboxPersona: ({
      body,
      typhonDomain,
      personaToken,
    }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.sandboxPersonas.put(personaToken)}`,
      {
        body: JSON.stringify(body),
        method: 'PUT',
      }),
    deleteSandboxPersonas: async ({ typhonDomain, personaToken }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.sandboxPersonas.archive(personaToken)}`,
      {
        method: 'PATCH',
      }),
    getWorkflowSandboxPersonas: async ({ typhonDomain, workflowToken }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.sandboxPersonas.getWorkflowSandboxPersonas(workflowToken)}`),
    checkSandboxPersonaNamesForConflicts: ({
      body,
      typhonDomain,
    }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.sandboxPersonas.checkSandboxPersonaNamesForConflicts}`, {
      body: JSON.stringify(body),
      method: 'POST',
    }),
  },

  whatIf: {
    evaluations: ({ analysisToken, params, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.whatIf.evaluations(analysisToken)}${params}`,
    ),
    preReqs: ({ workflowToken, versionId }) => ajaxFetch(`${routes.applications.whatIf(workflowToken)}${buildUrlParamsString({
      application_token: workflowToken,
      public_version: versionId,
      is_for_version_selector: true,
    })}`),
  },

  workflows: {
    create: ({
      workflowType,
      workflowName,
      isTestWorkflow,
      skuId = undefined,
    }) => {
      const formData = new FormData();
      formData.append('app_type', workflowType);
      formData.append('app_name', workflowName);
      formData.append('is_backtest', isTestWorkflow);
      if (skuId) {
        formData.append('workflow_sku_id', skuId);
      }

      return ajaxFetch(
        '/applications/',
        {
          body: formData,
          silent: true,
          method: 'POST',
        },
      );
    },

    clone: ({
      hasSilentAlertMessage,
      returnResponse = false,
      workflow: {
        actions,
        actionOptions,
        customerId,
        dataRoutes,
        isTestWorkflow,
        outcomes,
        reasonCodeGroups,
        relationships,
        services,
        tags,
        thresholds,
        workflowName,
        workflowType,
        sourceCustomerId,
        sourceWorkflowToken,
        sourceWorkflowPublicVersion,
        sdkConfig,
        sandboxPersonas,
      },
    }) => fetchDaffy('/admin/applications/clone/', {
      body: {
        actions,
        action_options: actionOptions,
        application_name: workflowName,
        application_type: workflowType,
        source_customer_id: sourceCustomerId,
        source_workflow_token: sourceWorkflowToken,
        source_workflow_public_version: sourceWorkflowPublicVersion,
        customer_id: customerId,
        data_routes: dataRoutes,
        is_backtest: isTestWorkflow,
        outcomes,
        reason_code_groups: reasonCodeGroups,
        relationships,
        services,
        tags,
        thresholds,
        sdk_config: sdkConfig,
        sandbox_personas: sandboxPersonas,
        is_federated: false,
      },
      hasSilentAlertMessage,
      method: 'POST',
      returnResponse,
    }),

    getCloningData: () => fetchDaffy(routes.admin.workflows.clone),

    getWorkflowCheckoutInfo: ({
      appToken,
      typhonDomain,
    }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.workflowCheckouts.workflowCheckout(appToken)}`),

    checkoutWorkflow: ({
      appToken,
      typhonDomain,
    }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.workflowCheckouts.workflowCheckout(appToken)}`, {
      method: 'POST',
    }),

    getWorkflowDetails: appToken => ajaxFetch(`${routes.applications.detailData(appToken)}?paginateBy=1000`, 'GET'),

    getWorkflowTestHistory: ({
      workflowToken,
      typhonDomain,
      queryParamsStr,
    }) => typhonAjaxFetch(`${typhonDomain}${routes.applications.workflowTestHistory}?workflow_token=${workflowToken}&${queryParamsStr}`),

    getWhatIfAnalysisData: ({
      analysisToken,
      typhonDomain,
    }) => typhonAjaxFetch(`${typhonDomain}${routes.applications.whatIfAnalysisData(analysisToken)}`),

    getWorkflows: params => ajaxFetch(`${routes.applications.listData}${buildUrlParamsString(params, true)}`),

    getWorkflowVersion: ({
      applicationToken,
      publicVersion,
    }) => ajaxFetch(routes.applications.versions.data(applicationToken, publicVersion)),

    getWorkflowProps: ({
      workflowToken,
    }) => ajaxFetch(routes.applications.versions.props(workflowToken)),

    setActiveVersion: ({
      applicationToken, hasSilentAlertMessage, versionNum,
    }) => apiCalls.workflows.update({
      applicationToken,
      body: {
        property: 'active_public_version',
        public_version: versionNum,
      },
      hasSilentAlertMessage,
    }),

    update: ({ applicationToken, body, hasSilentAlertMessage }) => fetchDaffy(
      `/applications/${applicationToken}/data/`,
      { body, hasSilentAlertMessage, method: 'POST' },
    ),

    /**
     * Get a list of available SKUs from the WorkflowSkus table
     * @returns All skus
     */
    getWorkflowSkus: ({ typhonDomain }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.workflow.skuCatalog}`),

    getWorkflowTaxonomy: ({ typhonDomain }) => typhonAjaxFetch(`${typhonDomain}${workflowRoutes.taxonomyCatalog}`),

    getWorkflowTaxonomyDetails: ({ typhonDomain, workflowToken }) => typhonAjaxFetch(
      `${typhonDomain}${workflowRoutes.details(workflowToken)}`,
    ),

    updateWorkflowDetails: ({
      typhonDomain,
      workflowToken,
      description,
      workflowEvaluationTypeIds,
    }) => typhonAjaxFetch(`${typhonDomain}${workflowRoutes.details(workflowToken)}`, {
      method: 'POST',
      body: JSON.stringify({
        description,
        workflow_evaluation_type_ids: workflowEvaluationTypeIds,
      }),
      silent: true,
    }, false, 0),

    /**
     * Creates a new workflow (Applications record) via Typhon, using a workflow taxonomy type
     * rather than a SKU.
     */
    createFromTaxonomy: ({
      typhonDomain,
      workflowName,
      workflowDescription,
      workflowTaxonomyTypeId,
      workflowEvaluationTypeIds,
      isBacktest,
    }) => typhonAjaxFetch(`${typhonDomain}${workflowRoutes.root}`, {
      method: 'POST',
      body: JSON.stringify({
        name: workflowName,
        description: workflowDescription,
        workflow_taxonomy_type_id: workflowTaxonomyTypeId,
        workflow_evaluation_type_ids: workflowEvaluationTypeIds,
        is_backtest: isBacktest,
      }),
      silent: true,
    }, false, 0),

    thresholdSimulator: {
      getBreakdown: ({
        typhonDomain,
        publicId,
        workflowVersion,
        startDate,
        endDate,
      }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.workflow.thresholdSimulator.getBreakdown({
        publicId,
        workflowVersion,
        startDate,
        endDate,
      })}`),
      getParams: ({
        typhonDomain,
        publicId,
        workflowVersion,
      }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.workflow.thresholdSimulator.getParams({
        publicId,
        workflowVersion,
      })}`),
    },
  },
  entityFeedback: {
    get: ({
      typhonDomain,
      queryParamsStr,
    }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.entityFeedback.get}${queryParamsStr}`),

    append: ({
      typhonDomain,
      payload,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.entityFeedback.append}`,
      {
        body: JSON.stringify(payload),
        method: 'POST',
      }),

    getOptions: ({ typhonDomain }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.entityFeedback.getOptions}`),
  },
  lists: {
    get: typhonDomain => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.lists.get}`,
    ),
    post: (
      {
        typhonDomain,
        payload,
      },
    ) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.lists.post}`,
      {
        body: JSON.stringify(payload),
        method: 'POST',
      },
    ),
    put: (
      {
        typhonDomain,
        payload,
        listToken,
      },
    ) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.lists.put(listToken)}`,
      {
        body: JSON.stringify(payload),
        method: 'PUT',
      },
    ),
    entities: {
      replace: ({
        typhonDomain,
        payload,
        listToken,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.lists.listToken.put(listToken)}`,
        {
          body: JSON.stringify(payload),
          method: 'PUT',
        }),
      append: ({
        typhonDomain,
        payload,
        listToken,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.lists.listToken.post(listToken)}`,
        {
          body: JSON.stringify(payload),
          method: 'POST',
        }),
      delete: body => fetchDaffy(routes.settings.lists.entities, { body, method: 'DELETE' }),
    },
  },

  publishedAttributes: {
    list: ({
      typhonDomain,
      queryParamsStr = '',
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.publishedAttributes.list}${queryParamsStr}`,
    ),
    get: ({ typhonDomain, attributeToken, queryParamsStr }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.publishedAttributes.get(attributeToken)}${queryParamsStr}`),
    post: ({ typhonDomain, body }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.publishedAttributes.post}`, {
      body: JSON.stringify(body),
      method: 'POST',
    }),
    patch: ({ typhonDomain, attributeToken, body }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.publishedAttributes.patch(attributeToken)}`, {
      body: JSON.stringify(body),
      method: 'PATCH',
    }),

    values: {
      get: ({ typhonDomain, attributeToken, entityToken }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.publishedAttributes.values.get(attributeToken)}`, {
        headers: { 'Alloy-Entity-Token': entityToken },
      }),
      post: ({ typhonDomain, attributeToken, body }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.publishedAttributes.values.post(attributeToken)}`, {
        body: JSON.stringify(body),
        method: 'POST',
      }),
    },

    groupings: {
      list: ({
        typhonDomain,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.publishedAttributes.groupings.list}`,
      ),
      post: ({
        typhonDomain,
        body,
      }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.publishedAttributes.groupings.post}`, {
        body: JSON.stringify(body),
        method: 'POST',
      }),
      patch: ({
        typhonDomain,
        groupingToken,
        body,
      }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.publishedAttributes.groupings.patch(groupingToken)}`, {
        body: JSON.stringify(body),
        method: 'PATCH',
      }),
    },
  },

  watchlists: {
    getMatches: async ({
      canResolve,
      entityToken,
      evaluationToken,
      typhonDomain,
    }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.watchlists.get({ canResolve, entityToken, evaluationToken })}`),
    postResolutions: async ({
      entityToken,
      evaluationToken,
      payload,
      typhonDomain,
    }) => (
      typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.watchlists.post({ entityToken, evaluationToken })}`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
      )
    ),
    postAIWatchlistResolver: async ({
      typhonDomain,
      evaluationToken,
      regenerate,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.watchlists.postAIWatchlistResolver({ evaluationToken, regenerate })}`,
      {
        method: 'POST',
        body: JSON.stringify({ regenerate }),
      },
    ),
    postAIBatch: async ({
      typhonDomain,
      resourceTokens,
      feature,
      isBacktest,
      initiator,
      batchConfig,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.watchlists.postAIBatch}`,
      {
        method: 'POST',
        body: JSON.stringify({
          resource_tokens: resourceTokens,
          feature,
          is_backtest: isBacktest,
          initiator,
          ...(batchConfig !== undefined && { batch_config: batchConfig }),
        }),
      },
    ),
    getBatchRunStatus: async ({ typhonDomain, runId }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.watchlists.getBatchRunStatus(runId)}`,
      {
        method: 'GET',
      },
    ),
    getBatchFeatures: async ({ typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.watchlists.getBatchFeatures}`,
      {
        method: 'GET',
      },
    ),
  },

  investigationAnalytics: {
    getAlertsOutcomes: ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigationAnalytics.getAlertsOutcomes}?${queryParamsStr}`,
    ),
    getAverageAge: ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigationAnalytics.getAverageAge}?${queryParamsStr}`,
    ),
    getInvestigationsClosedOpen: ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigationAnalytics.getInvestigationsClosedOpen}?${queryParamsStr}`,
    ),
    getInvestigationsOpenTotal: ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigationAnalytics.getInvestigationsOpenTotal}?${queryParamsStr}`,
    ),
    getAlertsOpenTotal: ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigationAnalytics.getAlertsOpenTotal}?${queryParamsStr}`,
    ),
    getInvestigationsStatus: ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigationAnalytics.getInvestigationsStatus}?${queryParamsStr}`,
    ),
    getTotalEventsAlertsSummary: ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigationAnalytics.getTotalEventsAlerts}?${queryParamsStr}`,
    ),
    getInvestigationsOutcomes: ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigationAnalytics.getInvestigationsOutcomes}?${queryParamsStr}`,
    ),
    getInvestigationsDailyVolume: ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigationAnalytics.getInvestigationsDailyVolume}?${queryParamsStr}`,
    ),
    getInvestigationsByAge: ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigationAnalytics.getInvestigationsByAge}?${queryParamsStr}`,
    ),
    getInvestigationsOutcomesFlow: ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigationAnalytics.getInvestigationsOutcomesFlow}?${queryParamsStr}`,
    ),
    getAlertsDailyVolume: ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigationAnalytics.getAlertsDailyVolume}?${queryParamsStr}`,
    ),
    getAlertsPerformance: ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigationAnalytics.getAlertsPerformance}?${queryParamsStr}`,
    ),
  },
  investigations: {
    deleteInvestigationType: ({ typhonDomain, investigationType }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.deletetInvestigationTypes(investigationType)}`,
      {
        method: 'DELETE',
      },
    ),
    archiveInvestigationType: ({ typhonDomain, payload }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.archiveInvestigationTypes}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    ),
    unarchiveInvestigationType: ({ typhonDomain, payload }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.unarchiveInvestigationTypes}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    ),
    getInvestigationsQueue: ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${routes.investigations.queue}?${queryParamsStr}`,
    ),
    getInvestigationsQueueFilters: ({ typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${routes.investigations.queueFilters}`,
    ),
    getInvestigation: ({ investigationToken, typhonDomain, queryParamsStr = '' }) => typhonAjaxFetch(
      `${typhonDomain}${routes.investigations.detail(investigationToken)}?${queryParamsStr}`,
    ),
    createInvestigationsQueueCSV: ({ filters, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${routes.investigations.csv}`,
      {
        method: 'POST',
        body: JSON.stringify({ filters }),
      },
    ),
    reviewAndClose: ({ investigationToken, payload, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${routes.investigations.review(investigationToken)}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    ),
    reviewInvestigation: ({ investigationToken, payload, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${routes.investigations.review(investigationToken)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    ),
    createInvestigationType: ({
      typhonDomain,
      payload,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.createInvestigationType}`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    ),
    getInvestigationTypes: ({
      typhonDomain,
      includeArchived = false,
      onlyPermittedTypes = false,
      archived = null,
      numRowsPerPage = null,
      pageNum = null,
      sortColumn = null,
      sortOrder = null,
      includeJourneys = false,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.getInvestigationTypes}${buildUrlParamsString({
        includeArchived,
        onlyPermittedTypes,
        q: JSON.stringify({
          archived,
        }),
        numRowsPerPage,
        pageNum,
        sortColumn,
        sortOrder,
        includeJourneys,
      })}`,
    ),
    getInvestigationTypeUseCases: ({
      typhonDomain,
    }) => typhonAjaxFetch(`${typhonDomain}${typhonRoutes.investigations.getInvestigationTypeUseCases}`),
    getInvestigationStateTypes: ({ typhonDomain, onlyAssignable = false }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.getInvestigationStateTypes}${buildUrlParamsString({ only_assignable: onlyAssignable })}`,
    ),
    getAssignableAgents: ({
      typhonDomain,
      investigationTypes,
      investigationStateTypes,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.assignableAgents}${buildUrlParamsString({
        q: JSON.stringify({
          types: investigationTypes,
          statuses: investigationStateTypes,
        }),
      })}`,
    ),
    getAlertAssignableAgents: ({
      typhonDomain,
      journeyTokens,
      journeyApplicationTokens,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.alertAssignableAgents}`,
      {
        method: 'POST',
        body: JSON.stringify({
          journeyTokens,
          journeyApplicationTokens,
        }),
      },
    ),
    assignAgent: ({ typhonDomain, payload }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.assignAgent}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    ),
    unassign: ({ typhonDomain, payload }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.unassign}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    ),
    alerts: {
      assignAgent: ({
        investigationToken,
        journeyApplicationToken,
        typhonDomain,
        payload,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.investigations.alerts.assignAgent({ investigationToken, journeyApplicationToken })}`,
        {
          method: 'PUT',
          body: JSON.stringify({ assigned_agent_token: payload.agent.token }),
        },
      ),
      submitAppendedReview: ({
        investigationToken,
        journeyApplicationToken,
        payload,
        typhonDomain,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.investigations.alerts.completeReview({ investigationToken, journeyApplicationToken })}`,
        {
          method: 'PUT',
          body: JSON.stringify(payload),
        },
      ),
      getData: ({
        investigationToken,
        journeyApplicationToken,
        typhonDomain,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.investigations.alerts.data({ investigationToken, journeyApplicationToken })}`,
      ),
      submitReview: ({
        investigationToken,
        journeyApplicationToken,
        payload,
        typhonDomain,
      }) => (
        typhonAjaxFetch(
          `${typhonDomain}${typhonRoutes.investigations.alerts.completeReview({
            investigationToken,
            journeyApplicationToken,
          })}`,
          {
            method: 'POST',
            body: JSON.stringify(payload),
          },
        )
      ),
      submitManualReview: ({
        alertManualReviewToken,
        investigationToken,
        journeyApplicationToken,
        payload,
        typhonDomain,
      }) => (
        typhonAjaxFetch(
          `${typhonDomain}${typhonRoutes.investigations.alerts.submitManualReview({
            alertManualReviewToken,
            investigationToken,
            journeyApplicationToken,
          })}`,
          {
            method: 'POST',
            body: JSON.stringify(payload),
          },
        )
      ),
      unassignAgent: ({
        investigationToken,
        journeyApplicationToken,
        typhonDomain,
      }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.investigations.alerts.unassignAgent({ investigationToken, journeyApplicationToken })}`,
        { method: 'PUT' },
      ),
      bulkManualReview: ({ investigationToken, typhonDomain, payload }) => (
        typhonAjaxFetch(
          `${typhonDomain}${typhonRoutes.investigations.bulkAlertReview({ investigationToken })}`,
          { method: 'POST', body: JSON.stringify(payload) },
        )
      ),
      bulkAppendedReview: ({ investigationToken, typhonDomain, payload }) => (
        typhonAjaxFetch(
          `${typhonDomain}${typhonRoutes.investigations.bulkAlertReview({ investigationToken })}`,
          { method: 'PUT', body: JSON.stringify(payload) },
        )
      ),
    },
    submitInvestigationNote: ({
      investigationToken,
      payload,
      typhonDomain,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.notes.saveNotes({ investigationToken })}`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    ),
    createInvestigation: ({ typhonDomain, payload }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.createInvestigation}`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    ),
    addEvidenceToInvestigation: ({ investigationToken, payload, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.addEvidence({ investigationToken })}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    ),
    getInvestigationSummary: ({ investigationToken, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.getInvestigationSummary({ investigationToken })}`,
    ),
    getInvestigationWebhookAuditEventDetails: ({
      investigationToken,
      webhookAuditEventToken,
      typhonDomain,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.getInvestigationWebhookAuditEventDetails({
        investigationToken,
        webhookAuditEventToken,
      })}`,
    ),
    archiveInvestigations: ({ typhonDomain, investigationTokens }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.archiveInvestigations}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          investigation_tokens: investigationTokens,
        }),
      },
    ),
    unarchiveInvestigations: ({ typhonDomain, investigationTokens }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.investigations.unarchiveInvestigations}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          investigation_tokens: investigationTokens,
        }),
      },
    ),
    getInvestigationEvidence: ({ investigationToken, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.evidence.fetch({ contextToken: investigationToken })}`,
    ),
    getInvestigationAccountsAndTransactions: ({
      investigationToken, typhonDomain,
    }) => typhonAjaxFetch(
      `${typhonDomain}${routes.investigations.accountsAndTransactions(investigationToken)}`,
    ),
  },

  ctrs: {
    createCtr: async ({ payload, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.ctr.create}`,
      { method: 'POST', body: JSON.stringify(payload) },
    ),
    updateCtr: async ({ payload, reportToken, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.ctr.update(reportToken)}`,
      { method: 'PUT', body: JSON.stringify(payload) },
    ),
    submitCtr: async ({ reportToken, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.ctr.submit(reportToken)}`,
      { method: 'POST' },
    ),
  },

  fraudAttackRadar: {
    createFARConfig: async ({ payload, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.fraudAttackRadar.configurations.create}`,
      { method: 'POST', body: JSON.stringify(payload), silent: true },
    ),
    updateFARConfig: async ({ payload, farConfigToken, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.fraudAttackRadar.configurations.update(farConfigToken)}`,
      { method: 'PATCH', body: JSON.stringify(payload), silent: true },
    ),
  },

  policyTests: {
    getPrerequisites: async ({
      typhonDomain, queryParamsStr,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.policyTests.getPrerequisites}${queryParamsStr}`,
    ),
    getTestResults: async ({ testToken, typhonDomain, queryParamsStr }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.policyTests.getTestResults(testToken)}?${queryParamsStr}`,
    ),
    getTestTypes: async ({ typhonDomain, queryParamsStr }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.policyTests.getTestTypes}${queryParamsStr}`,
    ),
    createConfig: async ({ payload, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.policyTests.createConfig}`,
      { method: 'POST', body: JSON.stringify(payload) },
    ),
    runTest: async ({ configToken, sampleSize = undefined, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.policyTests.runTest}`,
      {
        method: 'POST',
        body: JSON.stringify({
          configuration_token: configToken,
          ...(sampleSize && { sample_size: sampleSize }),
        }),
      },
    ),
    dataSample: async ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.policyTests.dataSample}${queryParamsStr}`,
    ),
    dataSampleFilters: async ({ typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.policyTests.dataSampleFilters}`,
    ),
  },

  customLists: {
    getAllLists: typhonDomain => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.customLists.get}`,
    ),
    createNewList: ({ typhonDomain, payload }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.customLists.post}`,
      { method: 'POST', body: JSON.stringify(payload) },
    ),
    updateListMeta: ({ typhonDomain, customListToken, payload }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.customLists.customListToken.patchMeta(customListToken)}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
    ),
    getVersions: ({ typhonDomain, customListToken }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.customLists.customListToken.getVersions(customListToken)}`,
    ),
    postVersion: ({ typhonDomain, customListToken, payload }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.customLists.customListToken.postVersion(customListToken)}`,
      { method: 'POST', body: JSON.stringify(payload) },
    ),
    // In-place append (OAK-1651): adds rows to the latest minor without minting a new version.
    // `silent` lets the calling hook own messaging (it distinguishes the 409 "being updated"
    // case); `retries: 0` because a POST /append is not idempotent — auto-retrying a
    // lost-but-applied write would duplicate the appended rows.
    appendToVersion: ({ typhonDomain, customListToken, payload }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.customLists.customListToken.append(customListToken)}`,
      { method: 'POST', body: JSON.stringify(payload), silent: true },
      false,
      0,
    ),
    getVersion: ({ typhonDomain, customListToken, version }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.customLists.customListToken.getVersion(customListToken, version)}`,
    ),
    activateVersion: ({ typhonDomain, customListToken, version }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.customLists.customListToken.activateVersion(customListToken, version)}`,
      { method: 'POST' },
    ),
  },

  clientQueryDataConfig: {
    getObjects: ({ typhonDomain }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.clientQueryDataConfig.getObjects}`,
    ),
  },
  dataSourcesModal: {
    getVendorApiCallsAndServiceResponses: ({
      typhonDomain,
      evaluationToken,
      queryParams,
    }) => typhonAjaxFetch(
      `${typhonDomain}${typhonRoutes.dataSourcesModal.getVendorApiCallsAndServiceResponses(evaluationToken, queryParams)}`,
    ),
  },
  workforceAnalytics: {
    onboarding: {
      getOnboardingSummary: ({ typhonDomain, queryParamsStr, timezone }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.workforceAnalytics.onboarding.onboardingSummary}${queryParamsStr}&timezone=${timezone}`,
      ),
      getOnboardingAgentPerformance: ({ queryParamsStr, typhonDomain }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.workforceAnalytics.onboarding.onboardingAgentPerformance}${queryParamsStr}`,
      ),
      getOnboardingTopKpis: ({ typhonDomain, queryParamsStr, timezone }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.workforceAnalytics.onboarding.onboardingTopKpis}${queryParamsStr}&timezone=${timezone}`,
      ),
    },
    ongoing: {
      getOngoingSummary: ({ typhonDomain, queryParamsStr, timezone }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.workforceAnalytics.ongoing.ongoingSummary}?${queryParamsStr}&timezone=${timezone}`,
      ),
      getOngoingAgentPerformance: ({ typhonDomain, queryParamsStr }) => typhonAjaxFetch(
        `${typhonDomain}${typhonRoutes.workforceAnalytics.ongoing.ongoingAgentPerformance}?${queryParamsStr}`,
      ),
    },
  },
  sar: {
    fetchSarCredentials: typhonDomain => typhonAjaxFetch(
      `${typhonDomain}/sar/sar-credentials`,
    ),
    createSarCredentials: (typhonDomain, data) => typhonAjaxFetch(
      `${typhonDomain}/sar/sar-credentials`,
      {
        method: 'POST',
        json: false,
        body: JSON.stringify({
          is_sandbox: data.mode === 'sandbox',
          username: data.username,
          password: data.password,
          host: data.host,
          port: data.port,
        }),
      },
    ),
    updateSarCredentials: (typhonDomain, data) => typhonAjaxFetch(
      `${typhonDomain}/sar/sar-credentials`,
      {
        method: 'PUT',
        json: false,
        body: JSON.stringify({
          is_sandbox: data.mode === 'sandbox',
          username: data.username,
          password: data.password,
          host: data.host,
          port: data.port,
        }),
      },
    ),
  },
};

export default apiCalls;
